import React, { useEffect, useState } from 'react';
import DialogBox from '../../common/DialogBox';
import CustomTable from '../../common/CustomTable';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import Button from '../../common/Button';
import { useDismissOnEscape } from '../../common/useDismissOnEscape';
import apiService from '../../../services/ApiService';
import { apiRoutes } from '../../../shared/apiRoutes';
import { formatDateOnly } from '../../../utils/util';
import { KeyIcon } from '@heroicons/react/20/solid';

interface TenantUser {
  id: number;
  userName: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  mobileNo?: string;
  isActive: boolean;
  role?: string;
  twoFactorEnabled?: boolean;
  createdOn?: string;
  lastLogin?: string;
}

interface ApiTenantUser {
  id?: number;
  userId?: number;
  userName?: string;
  user_name?: string;
  firstName?: string;
  first_name?: string;
  lastName?: string;
  last_name?: string;
  fullName?: string;
  full_name?: string;
  email?: string;
  mobileNo?: string;
  mobile_no?: string;
  phone?: string;
  isActive?: boolean | number;
  is_active?: boolean | number;
  active?: boolean | number;
  role?: string;
  Role?: string;
  roleName?: string;
  role_name?: string;
  createdOn?: string;
  created_on?: string;
  createdAt?: string;
  created_at?: string;
  lastLogin?: string;
  last_login?: string;
  lastLoginAt?: string;
  last_login_at?: string;
  [key: string]: any;
}

interface TenantUserListDialogProps {
  isOpen: boolean;
  tenantId: number;
  tenantName: string;
  onClose: () => void;
}

const TenantUserListDialog: React.FC<TenantUserListDialogProps> = ({
  isOpen,
  tenantId,
  tenantName,
  onClose,
}) => {
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState({ role: '', phoneNumber: '', userName: '' });
  const [resetUser, setResetUser] = useState<TenantUser | null>(null);
  const [resetting, setResetting] = useState(false);
  const [passwordUser, setPasswordUser] = useState<TenantUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  // Helper function to convert MySQL TINYINT(1) or number to boolean
  const toBoolean = (value: any, defaultValue: boolean = false): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (value === '1' || value === 'true' || value === true) return true;
    if (value === '0' || value === 'false' || value === false) return false;
    return defaultValue;
  };

  const mapApiUserToUser = (apiUser: ApiTenantUser): TenantUser => {
    return {
      id: apiUser.id ?? apiUser.userId ?? 0,
      userName: apiUser.userName || apiUser.user_name || '',
      firstName: apiUser.firstName || apiUser.first_name,
      lastName: apiUser.lastName || apiUser.last_name,
      fullName: apiUser.fullName || apiUser.full_name || 
                `${apiUser.firstName || apiUser.first_name || ''} ${apiUser.lastName || apiUser.last_name || ''}`.trim() ||
                apiUser.userName || apiUser.user_name || '',
      email: apiUser.email,
      mobileNo: apiUser.mobileNo || apiUser.mobile_no || apiUser.phone || '',
      isActive: toBoolean(apiUser.isActive ?? apiUser.is_active ?? apiUser.active, true),
      role: apiUser.role || apiUser.Role || apiUser.roleName || apiUser.role_name || '',
      twoFactorEnabled: Boolean(apiUser.twoFactorEnabled ?? apiUser.TwoFactorEnabled),
      createdOn: apiUser.createdOn || apiUser.created_on || apiUser.createdAt || apiUser.created_at,
      lastLogin: apiUser.lastLogin || apiUser.last_login || apiUser.lastLoginAt || apiUser.last_login_at,
    };
  };

  const fetchUsers = async () => {
    if (!tenantId) return;
    
    setLoading(true);
    try {
      const response = await apiService.get<ApiTenantUser[]>(apiRoutes.tenants.userList(tenantId));
      
      let usersData: ApiTenantUser[] = [];
      
      if (Array.isArray(response)) {
        usersData = response;
      } else {
        const responseObj = response as any;
        if (responseObj.data && Array.isArray(responseObj.data)) {
          usersData = responseObj.data;
        } else if (responseObj.items && Array.isArray(responseObj.items)) {
          usersData = responseObj.items;
        } else {
          console.warn('Unexpected response format:', response);
          usersData = [];
        }
      }

      const mappedUsers = usersData.map(mapApiUserToUser);
      setUsers(mappedUsers);
      applyFilters(mappedUsers);
    } catch (error) {
      console.error('Error fetching tenant users:', error);
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'Failed to load users for this tenant', type: 'error' }
      }));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (data: TenantUser[]) => {
    let filtered = [...data];

    if (filters.role) {
      filtered = filtered.filter(u =>
        (u.role || '').toLowerCase().includes(filters.role.toLowerCase())
      );
    }

    if (filters.phoneNumber) {
      filtered = filtered.filter(u =>
        (u.mobileNo || '').toLowerCase().includes(filters.phoneNumber.toLowerCase())
      );
    }

    if (filters.userName) {
      filtered = filtered.filter(u =>
        (u.userName || '').toLowerCase().includes(filters.userName.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
    setTotalPages(Math.ceil(filtered.length / rowsPerPage));
    setCurrentPage(0);
  };

  useEffect(() => {
    if (isOpen && tenantId) {
      fetchUsers();
    }
  }, [isOpen, tenantId]);

  useEffect(() => {
    applyFilters(users);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, rowsPerPage, users.length]);

  const confirmResetAuthenticator = async () => {
    if (!resetUser || resetting) return;
    setResetting(true);
    try {
      await apiService.post(apiRoutes.users.resetTwoFactor(resetUser.id), {});
      const clear = (user: TenantUser) => (
        user.id === resetUser.id ? { ...user, twoFactorEnabled: false } : user
      );
      setUsers((prev) => prev.map(clear));
      setFilteredUsers((prev) => prev.map(clear));
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: `Two-way authentication reset for ${resetUser.userName}`, type: 'success' },
      }));
      setResetUser(null);
    } catch (error: any) {
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: error?.message || error?.error || 'Could not reset two-way authentication', type: 'error' },
      }));
    } finally {
      setResetting(false);
    }
  };

  const closePasswordDialog = () => {
    if (resettingPassword) return;
    setPasswordUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setAdminPassword('');
    setPasswordError('');
  };
  const passwordDismissRef = useDismissOnEscape(passwordUser !== null, closePasswordDialog);

  const confirmResetPassword = async () => {
    if (!passwordUser || resettingPassword) return;
    const nextPassword = newPassword.trim();
    if (nextPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }
    if (nextPassword !== confirmPassword.trim()) {
      setPasswordError('Passwords do not match.');
      return;
    }
    if (!adminPassword.trim()) {
      setPasswordError('Your admin password is required.');
      return;
    }

    setResettingPassword(true);
    setPasswordError('');
    try {
      await apiService.post(apiRoutes.tenants.resetUserPassword(tenantId, passwordUser.id), {
        newPassword: nextPassword,
        adminPassword: adminPassword.trim(),
      });
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: `Password reset for ${passwordUser.userName}`, type: 'success' },
      }));
      setPasswordUser(null);
      setNewPassword('');
      setConfirmPassword('');
      setAdminPassword('');
      setPasswordError('');
    } catch (error: any) {
      const message = error?.message || error?.error || 'Could not reset password';
      if (error?.status === 401 || /invalid password|incorrect password/i.test(message)) {
        setPasswordError('Incorrect admin password');
      } else {
        setPasswordError(message);
      }
    } finally {
      setResettingPassword(false);
    }
  };

  const handleFilter = (appliedFilters: Record<string, string>) => {
    setFilters({
      role: appliedFilters['role'] || '',
      phoneNumber: appliedFilters['phoneNumber'] || '',
      userName: appliedFilters['userName'] || '',
    });
  };

  return (
    <DialogBox
      isOpen={isOpen}
      onClose={onClose}
      title={`Users for ${tenantName}`}
      panelClassName="w-[95vw] max-w-[1400px] h-[90vh] flex flex-col overflow-hidden"
      panelStyle={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
    >
      <>
      <div className="flex-1 overflow-auto p-1 min-h-0">
        <CustomTable<TenantUser>
          variant="gameList"
          data={filteredUsers.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage)}
          title=""
          columns={[
            { key: 'firstName', label: 'First Name', filterable: true },
            { key: 'lastName', label: 'Last Name', filterable: true },
            { key: 'userName', label: 'UserName', filterable: true },
            {
              key: 'role',
              label: 'Role',
              filterable: true,
              filterType: 'text',
              render: (item: TenantUser) => item.role || '—',
            },
            {
              key: 'mobileNo',
              label: 'Phone Number',
              filterable: true,
              filterType: 'text',
            },
            {
              key: 'email',
              label: 'Email',
              filterable: true,
            },
            {
              key: 'twoFactorEnabled',
              label: 'Authenticator',
              render: (item: TenantUser) => (
                <div className="flex items-center gap-2">
                  <span className={item.twoFactorEnabled ? 'text-green-700 font-medium' : 'text-slate-400'}>
                    {item.twoFactorEnabled ? 'On' : 'Off'}
                  </span>
                  {item.twoFactorEnabled && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setResetUser(item);
                      }}
                      className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                    >
                      Reset
                    </button>
                  )}
                </div>
              ),
            },
            {
              key: 'isActive',
              label: 'Status',
              render: (item: TenantUser) => (
                <span className={item.isActive ? 'text-green-600 font-medium' : 'text-gray-400'}>
                  {item.isActive ? 'Active' : 'Inactive'}
                </span>
              ),
            },
            {
              key: 'createdOn',
              label: 'Created On',
              render: (item: TenantUser) => 
                item.createdOn ? (formatDateOnly(item.createdOn) || '-') : '-',
            },
            {
              key: 'lastLogin',
              label: 'Last Login',
              render: (item: TenantUser) => 
                item.lastLogin ? (formatDateOnly(item.lastLogin) || '-') : '-',
            },
            {
              key: 'id',
              label: 'Actions',
              render: (item: TenantUser) => (
                <button
                  type="button"
                  title="Reset password"
                  onClick={(event) => {
                    event.stopPropagation();
                    setPasswordUser(item);
                    setNewPassword('');
                    setConfirmPassword('');
                    setAdminPassword('');
                    setPasswordError('');
                  }}
                  className="inline-flex items-center gap-1 rounded border border-purple-300 bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-800 hover:bg-purple-100"
                >
                  <KeyIcon className="h-3.5 w-3.5" />
                  Reset password
                </button>
              ),
            },
          ]}
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={setRowsPerPage}
          loading={loading}
          onFilter={handleFilter}
          getId={(user: TenantUser) => user.id}
        />
      </div>
      <ConfirmationDialog
        isOpen={resetUser !== null}
        title="Reset two-way authentication"
        message={resetUser
          ? `Reset two-way authentication for ${resetUser.userName}? They will sign in with username and password only. The linked phone stops working. They can turn it on again as a new setup.`
          : ''}
        onConfirm={() => { void confirmResetAuthenticator(); }}
        onCancel={() => { if (!resetting) setResetUser(null); }}
      />
      {passwordUser && (
        <div ref={passwordDismissRef} className="fixed inset-0 z-[10060] flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl space-y-3 text-left">
            <h3 className="text-sm font-semibold text-slate-900">
              Reset password for {passwordUser.userName}
            </h3>
            <p className="text-sm text-slate-600">
              Set a new login password for this tenant user. Their current session will end.
            </p>
            <div>
              <label htmlFor="tenant-user-new-password" className="block text-sm font-medium text-slate-700 mb-1">
                New password
              </label>
              <input
                id="tenant-user-new-password"
                type="password"
                value={newPassword}
                autoFocus
                autoComplete="new-password"
                disabled={resettingPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  setPasswordError('');
                }}
                className="w-full h-[34px] px-2 py-1 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="tenant-user-confirm-password" className="block text-sm font-medium text-slate-700 mb-1">
                Confirm new password
              </label>
              <input
                id="tenant-user-confirm-password"
                type="password"
                value={confirmPassword}
                autoComplete="new-password"
                disabled={resettingPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  setPasswordError('');
                }}
                className="w-full h-[34px] px-2 py-1 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="tenant-user-admin-password" className="block text-sm font-medium text-slate-700 mb-1">
                Your admin password
              </label>
              <input
                id="tenant-user-admin-password"
                type="password"
                value={adminPassword}
                autoComplete="current-password"
                disabled={resettingPassword}
                onChange={(event) => {
                  setAdminPassword(event.target.value);
                  setPasswordError('');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void confirmResetPassword();
                  }
                }}
                className="w-full h-[34px] px-2 py-1 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {passwordError ? <p className="text-xs text-red-600">{passwordError}</p> : null}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                onClick={closePasswordDialog}
                disabled={resettingPassword}
                className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => { void confirmResetPassword(); }}
                disabled={resettingPassword}
                className="bg-purple-700 hover:bg-purple-800 text-white px-4 py-2 rounded-md"
              >
                {resettingPassword ? 'Saving…' : 'Reset password'}
              </Button>
            </div>
          </div>
        </div>
      )}
      </>
    </DialogBox>
  );
};

export default TenantUserListDialog;
